import type { SecureMessage } from '../types'
import type {
  HuperBridgeQuote,
  HyperlaneBridgeAssetRequestType,
  HyperlaneEstimateQuoteRequestType,
  HyperlaneHistoryPatchTxStatusRequestType,
  HyperlaneHistoryTxPatchByTxOpHashesRequestType,
  HyperlaneHistoryTxSchemaType,
  HyperlaneTxListResponseType,
  WarpRouteType,
  WarpTypedTransaction
} from './types'

const API_URL =
  // checks for custom service url
  (typeof (window as any).__EMBARK_SERVICES__ !== 'undefined' && (window as any).__EMBARK_SERVICES__?.assetApiUrl) ||
  // checks for build-time injected service url
  (typeof __EMBARK_ASSET_API_URL__ !== 'undefined' && __EMBARK_ASSET_API_URL__) ||
  // should not fallback __EMBARK_ASSET_API_URL__ should be defined, remove later
  'https://api.embarkai.io/on-ramp/api'

type SendResponseFn = (messageId: string, data: any, targetOrigin: string) => void

interface PaginationParams {
  page?: number
  pageSize?: number
}

export class HyperlaneAPI {
  private sendResponseFn: SendResponseFn
  //   private sseAbortController: AbortController | null = null

  constructor(sendResponseFn: SendResponseFn) {
    this.sendResponseFn = sendResponseFn
  }

  // API METHODS
  /** available warp routes */
  private async getHyperlaneWarpRoutes(accessToken: string) {
    const result = await fetch(`${API_URL}/hyperlane/warp-routes`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${accessToken}` }
    })
    const warpRoutes = await result.json()

    return warpRoutes as WarpRouteType[]
  }
  /** bridging quote */
  private async estimateHyperlaneQuote(payload: HyperlaneEstimateQuoteRequestType, accessToken: string) {
    const result = await fetch(`${API_URL}/hyperlane/estimate-quote`, {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${accessToken}` }
    })
    const quote = await result.json()

    return quote as HuperBridgeQuote
  }
  /** list of pre-cooked TXs by Hyperlane SDK */
  private async bridgeHyperlaneAsset(payload: HyperlaneBridgeAssetRequestType, accessToken: string) {
    const result = await fetch(`${API_URL}/hyperlane/bridge-asset`, {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${accessToken}` }
    })

    const response = await result.json()

    return response as {
      txs: WarpTypedTransaction[]
      hyperlaneDbTx: HyperlaneHistoryTxSchemaType | null
    }
  }
  /** bridge history */
  private async getHyperlaneBridgeHistory(pagination: PaginationParams, accessToken: string) {
    const { page, pageSize } = pagination

    const url = new URL(`${API_URL}/hyperlane/bridge-history`)
    if (page) url.searchParams.append('page', page.toString())
    if (pageSize) url.searchParams.append('pageSize', pageSize.toString())

    const result = await fetch(url.toString(), { method: 'GET', headers: { Authorization: `Bearer ${accessToken}` } })

    const history = (await result.json()) as HyperlaneTxListResponseType

    return history
  }
  /** patch initiated bridge transaction */
  private async patchInitiatedBridgeTxStatus(payload: HyperlaneHistoryPatchTxStatusRequestType, accessToken: string) {
    const result = await fetch(`${API_URL}/hyperlane/patch-bridge-tx-status`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${accessToken}` }
    })
    const response = await result.json()

    return response as { success: boolean; message: string }
  }

  private async patchInitiatedBridgeTxOpHashes(
    payload: HyperlaneHistoryTxPatchByTxOpHashesRequestType,
    accessToken: string
  ) {
    const result = await fetch(`${API_URL}/hyperlane/patch-bridge-op-hashes`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${accessToken}` }
    })
    const response = await result.json()

    return response as { ok: boolean; message: string }
  }

  // IFRAME MESSAGING

  public async handleMessage(message: SecureMessage, origin: string): Promise<void> {
    const { type, messageId, data } = message

    const accessToken = data.accessToken || ''

    console.info('[ HYPERLANE Received message ]', type, data)

    switch (type) {
      case 'HYPERLANE_GET_WARP_ROUTES_QUERY':
        const warpRoutes = await this.getHyperlaneWarpRoutes(accessToken)
        this.sendResponseFn(messageId, warpRoutes, origin)

        break

      case 'HYPERLANE_ESTIMATE_QUOTE_QUERY':
        const quote = await this.estimateHyperlaneQuote(data.payload, accessToken)
        this.sendResponseFn(messageId, quote, origin)

        break

      case 'HYPERLANE_BRIDGE_TRANSACTIONS_QUERY':
        const limitsSummaryResponse = await this.bridgeHyperlaneAsset(data.payload, accessToken)
        this.sendResponseFn(messageId, limitsSummaryResponse, origin)

        break

      case 'HYPERLANE_TRANSACTIONS_HISTORY_QUERY':
        const historyResponse = await this.getHyperlaneBridgeHistory(data.payload, accessToken)
        this.sendResponseFn(messageId, historyResponse, origin)

        break

      case 'HYPERLANE_PATCH_TRANSACTION_STATUS_QUERY':
        const patchResponse = await this.patchInitiatedBridgeTxStatus(data.payload, accessToken)
        this.sendResponseFn(messageId, patchResponse, origin)

        break

      case 'HYPERLANE_PATCH_TRANSACTION_OP_HASHES_QUERY':
        const patchbyTxOpHashesResponse = await this.patchInitiatedBridgeTxOpHashes(data.payload, accessToken)
        this.sendResponseFn(messageId, patchbyTxOpHashesResponse, origin)

        break

      default:
        throw new Error(`[FINCHPAY]: Unknown message type: ${type}`)
    }
  }
}
